import { toast } from 'sonner';
import { useRef, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import axios, { endpoints } from 'src/utils/axios';
import {
  onForegroundPush,
  getWebPushToken,
  isWebPushConfigured,
  requestWebPushPermission,
} from 'src/lib/firebase-web-push';

const DEVICE_ID_KEY = 'pl_web_device_id';
const REGISTERED_TOKEN_KEY = 'pl_web_fcm_token';

function getOrCreateDeviceId() {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? `web_${crypto.randomUUID()}`
        : `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Register an FCM web push token for the current authenticated user and wire
 * up a foreground message handler that surfaces a toast and routes to chat
 * on click. Idempotent — safe to call once per session; skips work when web
 * push is misconfigured, the browser doesn't support it, or permission has
 * been denied.
 */
export function useWebPush({ enabled = true } = {}) {
  const router = useRouter();
  const startedRef = useRef(false);
  const unsubscribeRef = useRef(null);

  const handleForeground = useCallback(
    (payload) => {
      const data = payload?.data || {};
      const title = payload?.notification?.title || data.title || 'New message';
      const body = payload?.notification?.body || data.body || '';
      const isChat = data.type === 'CHAT_MESSAGE' || data.screen === 'chat';

      toast(title, {
        description: body,
        action: isChat
          ? {
              label: 'Open',
              onClick: () => router.push(paths.dashboard.chat.root),
            }
          : undefined,
      });
    },
    [router],
  );

  useEffect(() => {
    if (!enabled || startedRef.current) return undefined;
    if (!isWebPushConfigured()) return undefined;
    startedRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const permission = await requestWebPushPermission();
        if (cancelled || permission !== 'granted') return;

        const token = await getWebPushToken();
        if (cancelled || !token) return;

        const deviceId = getOrCreateDeviceId();
        const previouslyRegistered = localStorage.getItem(REGISTERED_TOKEN_KEY);

        if (previouslyRegistered !== token) {
          await axios.post(endpoints.auth.registerDevice, {
            device_type: 'web',
            device_id: deviceId,
            fcm_token: token,
            os_version:
              typeof navigator !== 'undefined' ? navigator.userAgent : 'web',
          });
          localStorage.setItem(REGISTERED_TOKEN_KEY, token);
        }

        const unsubscribe = await onForegroundPush(handleForeground);
        if (cancelled) {
          unsubscribe?.();
        } else {
          unsubscribeRef.current = unsubscribe;
        }
      } catch (err) {
        // Swallow — web push is best-effort. Surface to console for debugging.
        console.warn('[useWebPush] setup failed', err);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [enabled, handleForeground]);
}
